/**
 * TeachFirstRenderer - dispatches a single item to the right "archetype" view
 * for teacher-led, paced-by-teacher pre-game teaching. Render rules come from
 * TEACH_FIRST_SPEC.md (10 archetypes covering 6,971 browse items).
 *
 * Render contract: dispatch by `exercise_type` (NOT by game). la_course alone
 * hosts 19 exercise_types - same game, very different items.
 *
 * Sizing contract: every dimension uses CSS clamp() so the card looks right
 * from a portrait iPad (~768px wide) up to a smartboard (~1920px+) without
 * scrolling the prompt out of view.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Volume2, Square, Eye } from 'lucide-react';
import { useS3Audio } from '../../hooks/useS3Audio';
import { ContentImage } from '../../lib/GameVisuals';

// ─────────────────────────────────────────────────────────────────────────────
// Archetype mapping (per TEACH_FIRST_SPEC.md)
// ─────────────────────────────────────────────────────────────────────────────
const MCQ_TYPES = new Set([
  'word_image_speed_match', 'article_selection', 'tense_sorting',
  'comparison_selection', 'auxiliary_selection', 'verb_selection',
  'preposition_selection', 'sentence_transformation', 'agreement_selection',
  'tense_identification', 'article_preposition_selection',
  'conjunction_selection', 'negation_selection', 'passe_compose_completion',
  'possessive_selection', 'cultural_knowledge',
]);
const NUMBER_TIME_TYPES = new Set(['number_reading', 'time_reading']);
const LISTENING_TYPES = new Set(['listening_identification', 'dialogue_completion']);
const CULTURAL_MAP_TYPES = new Set(['cultural_knowledge_quiz', 'cultural_trivia', 'direction_following']);

export function classifyArchetype(item) {
  const et = item?.exercise_type || '';
  if (et === 'conjugation_drill') return 'conjugation';
  if (NUMBER_TIME_TYPES.has(et)) return 'number_time';
  if (et === 'matching_pairs') return 'matching_pairs';
  if (et === 'category_sorting' || et === 'category_recognition') return 'category';
  if (LISTENING_TYPES.has(et)) return 'listening';
  if (et === 'pronunciation_reading') return 'reading_passage';
  if (et === 'sentence_construction') return 'sentence_construction';
  if (et === 'sequence_ordering') return 'sequence';
  if (CULTURAL_MAP_TYPES.has(et)) return 'cultural_map';
  if (MCQ_TYPES.has(et)) return 'mcq';
  // Fallback heuristics (data without exercise_type or unmapped strings)
  if (item?.mode === 'see_produce_french' || item?.mode === 'see_clock_produce_french' || item?.number != null || item?.time_display) return 'number_time';
  if (Array.isArray(item?.options) && item.options.length > 0) return 'mcq';
  if (Array.isArray(item?.pairs)) return 'matching_pairs';
  if (Array.isArray(item?.words)) return 'reading_passage';
  if (item?.audio_text_fr) return 'listening';
  return 'mcq';
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styling primitives
// ─────────────────────────────────────────────────────────────────────────────
const COLORS = {
  bg: '#F7F8FC',
  surface: '#FFFFFF',
  border: '#E4E7EE',
  textPrimary: '#1E2140',
  textSecondary: '#5B6275',
  accent: '#4C6EF5',
  accentSoft: '#EAEEFF',
  green: '#0CA678',
  greenSoft: '#E6F8F1',
  amber: '#F59F00',
};

const cardStyle = {
  background: COLORS.surface,
  border: `2px solid ${COLORS.border}`,
  borderRadius: '1.25rem',
  padding: 'clamp(1.25rem, 2.5vw, 3rem) clamp(1.25rem, 2.5vw, 2.75rem)',
  width: '100%',
  maxWidth: 1100,
  boxShadow: '0 8px 30px rgba(30,33,64,0.08)',
};

const labelChip = {
  display: 'inline-block',
  padding: '0.3rem 0.85rem',
  borderRadius: '999px',
  background: COLORS.accentSoft,
  color: COLORS.accent,
  fontFamily: 'Nunito, sans-serif',
  fontSize: 'clamp(0.72rem, 0.9vw, 0.95rem)',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const ptH1 = { fontFamily: 'Fredoka, sans-serif', fontSize: 'clamp(1.5rem, 2.6vw, 3rem)', fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.2, margin: '0.5rem 0' };
const ptH2 = { fontFamily: 'Fredoka, sans-serif', fontSize: 'clamp(1.15rem, 1.7vw, 2rem)', fontWeight: 600, color: COLORS.textPrimary, margin: '0.35rem 0' };
const ptGloss = { fontFamily: 'Nunito, sans-serif', fontSize: 'clamp(0.95rem, 1.2vw, 1.5rem)', color: COLORS.textSecondary, fontStyle: 'italic', marginTop: '0.5rem' };

const pickFR = (item) => (item?.prompt_text_fr || '').trim();
const pickEN = (item) => (item?.prompt_text_en || '').trim();

// ─────────────────────────────────────────────────────────────────────────────
// Archetype components
// ─────────────────────────────────────────────────────────────────────────────

function McqView({ item }) {
  const [revealed, setRevealed] = useState(false);
  const fr = pickFR(item);
  const en = pickEN(item);
  const isImage = item?.prompt_type === 'image' && item?.prompt_image;
  const rawOptions = Array.isArray(item?.options) ? item.options : [];
  // Shuffle options so option position is not a memorization cue between
  // Teach First and the live game. Stable within one mount via useState's
  // lazy initializer (the parent dispatcher re-keys per item_id, so a new
  // item gets a new mount and a new shuffle).
  const [options] = useState(() => {
    if (rawOptions.length < 2) return rawOptions;
    const arr = rawOptions.slice();
    for (let k = arr.length - 1; k > 0; k--) {
      const j = Math.floor(Math.random() * (k + 1));
      [arr[k], arr[j]] = [arr[j], arr[k]];
    }
    return arr;
  });
  const correctIdx = options.findIndex(o => o?.correct);

  return (
    <div data-testid="teach-mcq-view" style={cardStyle}>
      <span style={labelChip}>Question</span>
      {isImage && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: 'clamp(0.75rem, 1.5vw, 1.5rem) 0' }}>
          <ContentImage
            src={item.prompt_image}
            alt={fr || en || 'prompt'}
            size={'min(280px, 28vh)'}
            style={{ width: 'min(280px, 28vh)', height: 'min(280px, 28vh)' }}
          />
        </div>
      )}
      {/* Live-game pattern: when both EN and FR exist, FR is the big heading
          and EN sits small above as a gloss. When only one exists, that's
          the heading. */}
      {fr ? (
        <>
          {en && <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: 'clamp(0.9rem, 1.1vw, 1.25rem)', color: COLORS.textSecondary, fontWeight: 600, marginTop: '0.25rem' }}>{en}</div>}
          <div style={ptH1}>{fr}</div>
        </>
      ) : (
        <div style={ptH1}>{en || '—'}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: options.length > 4 ? '1fr 1fr' : '1fr', gap: 'clamp(0.5rem, 0.9vw, 0.9rem)', marginTop: 'clamp(1rem, 1.8vw, 2rem)' }}>
        {options.map((opt, i) => {
          const isCorrect = i === correctIdx;
          const show = revealed;
          const bg = !show ? COLORS.surface : (isCorrect ? COLORS.greenSoft : COLORS.surface);
          const border = !show ? COLORS.border : (isCorrect ? COLORS.green : COLORS.border);
          const opacity = !show ? 1 : (isCorrect ? 1 : 0.4);
          const text = opt?.text || opt?.label || '';
          return (
            <div key={i} data-testid={`teach-mcq-opt-${i}`} style={{
              padding: 'clamp(0.7rem, 1.2vw, 1.25rem) clamp(0.9rem, 1.5vw, 1.5rem)', borderRadius: '0.9rem', border: `2px solid ${border}`,
              background: bg, color: COLORS.textPrimary, opacity,
              fontFamily: 'Nunito, sans-serif', fontSize: 'clamp(1rem, 1.4vw, 1.65rem)', fontWeight: isCorrect && show ? 700 : 500,
              transition: 'all 0.2s',
            }}>
              {text}
              {show && isCorrect && <span style={{ marginLeft: '0.75rem', color: COLORS.green, fontWeight: 700, fontSize: 'clamp(1.1rem, 1.5vw, 1.8rem)' }}>✓</span>}
            </div>
          );
        })}
      </div>

      {!revealed && options.length > 0 && (
        <button data-testid="teach-mcq-reveal" onClick={() => setRevealed(true)} style={{
          marginTop: 'clamp(1rem, 1.8vw, 2rem)', padding: 'clamp(0.6rem, 1vw, 0.9rem) clamp(1.25rem, 2vw, 2rem)', borderRadius: '999px', border: 'none',
          background: COLORS.accent, color: 'white', fontFamily: 'Fredoka, sans-serif',
          fontSize: 'clamp(0.95rem, 1.2vw, 1.3rem)', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <Eye size={20} /> Reveal answer
        </button>
      )}
    </div>
  );
}

function ConjugationView({ item }) {
  const verb = item?.verb || '';
  const subject = item?.subject || '';
  const correctForm = item?.correct_form || '';
  const cloze = pickFR(item);
  const en = pickEN(item);
  // Fill the blank in the cloze with the correct form (best effort)
  const filled = useMemo(() => {
    if (!cloze) return correctForm;
    return cloze.replace(/_+/g, correctForm).replace(/\.\.\.+/g, correctForm);
  }, [cloze, correctForm]);

  return (
    <div data-testid="teach-conjugation-view" style={cardStyle}>
      <span style={labelChip}>Conjugation</span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginTop: '0.75rem' }}>
        <span style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>verbe</span>
        <span style={{ fontFamily: 'Fredoka', fontSize: '2.4rem', fontWeight: 700, color: COLORS.textPrimary }}>{verb || '—'}</span>
        {subject && <span style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: COLORS.textSecondary }}>· {subject}</span>}
      </div>
      <div style={{ ...ptH1, marginTop: '1.5rem' }}>{filled || '—'}</div>
      {en && <div style={ptGloss}>{en}</div>}
      {correctForm && (
        <div style={{ marginTop: '1.25rem', display: 'inline-block', padding: '0.7rem 1.4rem', borderRadius: '0.7rem', background: COLORS.greenSoft, color: COLORS.green, fontFamily: 'Fredoka', fontWeight: 600, fontSize: '1.6rem' }}>
          {subject ? `${subject} ` : ''}{correctForm}
        </div>
      )}
    </div>
  );
}

function NumberTimeView({ item }) {
  const hasNumber = item?.number != null || item?.french_word;
  const hasTime = item?.time_display || item?.time_french;

  if (hasTime) {
    return (
      <div data-testid="teach-time-view" style={{ ...cardStyle, textAlign: 'center' }}>
        <span style={labelChip}>Heure</span>
        <div style={{ ...ptH1, fontSize: 'clamp(2.5rem, 5.5vw, 5rem)', marginTop: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}>{item.time_display || '—'}</div>
        <div style={{ ...ptH2, color: COLORS.accent, marginTop: '0.5rem', fontSize: 'clamp(1.4rem, 2.8vw, 2.5rem)' }}>{item.time_french || ''}</div>
      </div>
    );
  }
  if (hasNumber) {
    return (
      <div data-testid="teach-number-view" style={{ ...cardStyle, textAlign: 'center' }}>
        <span style={labelChip}>Nombre</span>
        <div style={{ ...ptH1, fontSize: 'clamp(3rem, 7vw, 6.5rem)', marginTop: 'clamp(0.75rem, 1.5vw, 1.5rem)' }}>{item.number != null ? item.number : '—'}</div>
        <div style={{ ...ptH2, color: COLORS.accent, marginTop: '0.5rem', fontSize: 'clamp(1.5rem, 3vw, 2.75rem)' }}>{item.french_word || ''}</div>
      </div>
    );
  }
  return <McqView item={item} />;
}

function MatchingPairsView({ item }) {
  const pairs = Array.isArray(item?.pairs) ? item.pairs : [];
  return (
    <div data-testid="teach-matching-view" style={cardStyle}>
      <span style={labelChip}>Paires</span>
      {item?.title && <div style={{ ...ptH2, marginTop: '0.5rem' }}>{item.title}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.9rem 1.5rem', marginTop: '1.5rem' }}>
        {pairs.map((p, i) => (
          <React.Fragment key={i}>
            <div style={{ padding: '1rem 1.25rem', background: COLORS.accentSoft, borderRadius: '0.7rem', fontFamily: 'Nunito', fontWeight: 600, color: COLORS.textPrimary, fontSize: '1.45rem' }}>{p?.card_a || ''}</div>
            <div style={{ padding: '1rem 1.25rem', background: COLORS.greenSoft, borderRadius: '0.7rem', fontFamily: 'Nunito', fontWeight: 600, color: COLORS.textPrimary, fontSize: '1.45rem' }}>{p?.card_b || ''}</div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function CategoryView({ item }) {
  // category_sorting OR category_recognition
  if (item?.exercise_type === 'category_recognition') {
    const target = item?.target_category_fr || item?.target_instruction || '';
    const words = Array.isArray(item?.correct_words) ? item.correct_words : [];
    return (
      <div data-testid="teach-category-view" style={cardStyle}>
        <span style={labelChip}>Catégorie</span>
        <div style={{ ...ptH2, marginTop: '0.75rem' }}>{target}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginTop: '1.25rem' }}>
          {words.map((w, i) => (
            <span key={i} style={{ padding: '0.6rem 1.1rem', borderRadius: '999px', background: COLORS.greenSoft, color: COLORS.green, fontFamily: 'Nunito', fontWeight: 700, fontSize: '1.35rem' }}>{w}</span>
          ))}
        </div>
      </div>
    );
  }
  // category_sorting
  const categories = Array.isArray(item?.categories) ? item.categories : [];
  const items = Array.isArray(item?.items) ? item.items : [];
  const groupBy = (catIndex) => items.filter(it => it?.category === catIndex || it?.category === categories[catIndex]?.label);
  return (
    <div data-testid="teach-category-view" style={cardStyle}>
      <span style={labelChip}>Tri</span>
      {item?.title && <div style={{ ...ptH2, marginTop: '0.75rem' }}>{item.title}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: categories.length > 2 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', gap: '1rem', marginTop: '1.5rem' }}>
        {categories.map((c, ci) => (
          <div key={ci} style={{ border: `2px solid ${COLORS.border}`, borderRadius: '0.9rem', padding: '1rem' }}>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.45rem', fontWeight: 700, color: COLORS.accent, marginBottom: '0.75rem' }}>{c?.label}</div>
            {groupBy(ci).map((g, gi) => (
              <div key={gi} style={{ fontFamily: 'Nunito', color: COLORS.textPrimary, fontSize: '1.25rem', padding: '0.3rem 0' }}>· {g?.display || g?.text}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ListeningView({ item }) {
  const { play, stop, isPlaying } = useS3Audio();
  const audioText = item?.audio_text_fr || '';
  const correctOpt = (Array.isArray(item?.options) ? item.options : []).find(o => o?.correct);
  const correctText = correctOpt?.text || correctOpt?.label || '';
  useEffect(() => { return () => stop(); }, [stop]);
  useEffect(() => { stop(); }, [item?.item_id, stop]);

  const handlePlay = () => {
    if (isPlaying) { stop(); return; }
    // S3 path used by the live game for listening items
    play('detective', item?.item_id, audioText);
  };

  return (
    <div data-testid="teach-listening-view" style={cardStyle}>
      <span style={labelChip}>Écoute</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.75rem, 1.5vw, 1.5rem)', marginTop: 'clamp(0.75rem, 1.5vw, 1.5rem)', flexWrap: 'wrap' }}>
        <button data-testid="teach-listening-play" onClick={handlePlay} style={{
          width: 'clamp(64px, 7vw, 96px)', height: 'clamp(64px, 7vw, 96px)', borderRadius: '50%', border: 'none',
          background: isPlaying ? COLORS.amber : COLORS.accent, color: 'white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(76,110,245,0.4)', flexShrink: 0,
        }} aria-label={isPlaying ? 'Stop audio' : 'Play audio'}>
          {isPlaying ? <Square size={28} fill="white" /> : <Volume2 size={36} />}
        </button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: 'Nunito', fontSize: 'clamp(0.78rem, 0.95vw, 1.05rem)', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Audio (français)</div>
          <div style={{ ...ptH2, marginTop: '0.35rem' }}>&ldquo;{audioText || '—'}&rdquo;</div>
        </div>
      </div>
      {correctText && (
        <div style={{ marginTop: 'clamp(1rem, 1.5vw, 1.75rem)', padding: 'clamp(0.7rem, 1vw, 1rem) clamp(0.9rem, 1.3vw, 1.25rem)', borderRadius: '0.7rem', background: COLORS.greenSoft, color: COLORS.green, fontFamily: 'Nunito', fontWeight: 700, fontSize: 'clamp(1.05rem, 1.3vw, 1.4rem)' }}>
          Réponse : {correctText}
        </div>
      )}
    </div>
  );
}

function ReadingPassageView({ item }) {
  const words = Array.isArray(item?.words) ? item.words : [];
  const passage = words.length ? words.join(' ') : (item?.completed_sentence || '');
  return (
    <div data-testid="teach-passage-view" style={cardStyle}>
      <span style={labelChip}>Passage</span>
      {item?.title && <div style={{ ...ptH2, marginTop: '0.5rem' }}>{item.title}</div>}
      <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '2.2rem', lineHeight: 1.55, color: COLORS.textPrimary, marginTop: '1.25rem' }}>
        {passage}
      </div>
      {item?.title_en && <div style={ptGloss}>{item.title_en}</div>}
    </div>
  );
}

function SentenceConstructionView({ item }) {
  const sentence = item?.completed_sentence || pickFR(item) || '';
  const en = item?.english_meaning || pickEN(item) || '';
  const hint = item?.hint || '';
  return (
    <div data-testid="teach-sentence-view" style={cardStyle}>
      <span style={labelChip}>Phrase</span>
      <div style={{ ...ptH1, marginTop: '1rem' }}>{sentence || '—'}</div>
      {en && <div style={ptGloss}>{en}</div>}
      {hint && (
        <div style={{ marginTop: '1.5rem', padding: '0.75rem 1.1rem', background: COLORS.accentSoft, borderRadius: '0.7rem', color: COLORS.accent, fontFamily: 'Nunito', fontSize: '1.35rem' }}>
          Indice : {hint}
        </div>
      )}
    </div>
  );
}

function SequenceView({ item }) {
  const steps = Array.isArray(item?.steps) ? item.steps : [];
  const ingredients = Array.isArray(item?.ingredients_needed) ? item.ingredients_needed : [];
  return (
    <div data-testid="teach-sequence-view" style={cardStyle}>
      <span style={labelChip}>Recette</span>
      <div style={{ ...ptH2, marginTop: '0.75rem' }}>{item?.recipe_name_fr || ''}</div>
      {item?.recipe_name_en && <div style={ptGloss}>{item.recipe_name_en}</div>}
      {ingredients.length > 0 && (
        <div style={{ marginTop: '1rem', fontFamily: 'Nunito', color: COLORS.textSecondary, fontSize: '1.3rem' }}>
          <strong>Ingrédients :</strong> {ingredients.join(', ')}
        </div>
      )}
      <ol style={{ marginTop: '1.25rem', paddingLeft: '1.5rem', fontFamily: 'Nunito', fontSize: '1.3rem' }}>
        {steps.map((s, i) => (
          <li key={i} style={{ marginBottom: '0.9rem', color: COLORS.textPrimary }}>
            <div>{s?.instruction_fr || ''}</div>
            {s?.instruction_en && <div style={{ color: COLORS.textSecondary, fontStyle: 'italic', fontSize: '1.1rem' }}>{s.instruction_en}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function CulturalMapView({ item }) {
  if (item?.exercise_type === 'direction_following') {
    return (
      <div data-testid="teach-direction-view" style={cardStyle}>
        <span style={labelChip}>Carte</span>
        <div style={{ ...ptH2, marginTop: '0.75rem' }}>
          {item?.start_location || '?'} → {item?.destination || '?'}
        </div>
        <div style={{ ...ptH1, fontSize: '2.2rem', marginTop: '1rem' }}>{item?.direction_text_fr || ''}</div>
        {item?.direction_text_en && <div style={ptGloss}>{item.direction_text_en}</div>}
      </div>
    );
  }
  const correctOpt = (Array.isArray(item?.options) ? item.options : []).find(o => o?.correct);
  const fact = item?.fact_fr || item?.fact_popup || '';
  const factEn = item?.fact_en || '';
  const province = item?.target_province || item?.map_location || '';
  return (
    <div data-testid="teach-cultural-view" style={cardStyle}>
      <span style={labelChip}>Culture</span>
      <div style={{ ...ptH2, marginTop: '0.75rem' }}>{pickFR(item) || pickEN(item) || '—'}</div>
      {correctOpt && (
        <div style={{ marginTop: '1rem', display: 'inline-block', padding: '0.7rem 1.4rem', borderRadius: '0.7rem', background: COLORS.greenSoft, color: COLORS.green, fontFamily: 'Fredoka', fontWeight: 700, fontSize: '1.6rem' }}>
          {correctOpt.text || correctOpt.label}
        </div>
      )}
      {fact && (
        <div style={{ marginTop: '1.25rem', padding: '1rem 1.25rem', background: COLORS.accentSoft, borderRadius: '0.7rem' }}>
          <div style={{ fontFamily: 'Nunito', color: COLORS.textPrimary, fontSize: '1.3rem' }}>{fact}</div>
          {factEn && <div style={{ fontFamily: 'Nunito', color: COLORS.textSecondary, fontStyle: 'italic', marginTop: '0.4rem', fontSize: '1.15rem' }}>{factEn}</div>}
        </div>
      )}
      {province && <div style={{ marginTop: '1rem', fontFamily: 'Nunito', color: COLORS.textSecondary, fontSize: '1.15rem' }}>📍 {province}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Public dispatcher
// ─────────────────────────────────────────────────────────────────────────────
export function TeachFirstRenderer({ item }) {
  if (!item) return null;
  const archetype = classifyArchetype(item);
  // Use item_id as key so each archetype subcomponent re-mounts when the
  // teacher steps to a new item (resets internal state like MCQ "revealed").
  const key = item?.item_id || `${archetype}-${item?.prompt_text_fr || item?.audio_text_fr || ''}`;
  switch (archetype) {
    case 'mcq': return <McqView key={key} item={item} />;
    case 'conjugation': return <ConjugationView key={key} item={item} />;
    case 'number_time': return <NumberTimeView key={key} item={item} />;
    case 'matching_pairs': return <MatchingPairsView key={key} item={item} />;
    case 'category': return <CategoryView key={key} item={item} />;
    case 'listening': return <ListeningView key={key} item={item} />;
    case 'reading_passage': return <ReadingPassageView key={key} item={item} />;
    case 'sentence_construction': return <SentenceConstructionView key={key} item={item} />;
    case 'sequence': return <SequenceView key={key} item={item} />;
    case 'cultural_map': return <CulturalMapView key={key} item={item} />;
    default: return <McqView key={key} item={item} />;
  }
}
